/**
 * The three ways to get a picture into support, as one control.
 *
 * Photograph the page you go to next, photograph another tab or the whole
 * screen, or upload a file. Shared by the composer, where they sit in a row
 * beside the pictures already taken, and by a reply in the bubble, where they
 * appear in a popover under the paperclip.
 *
 * One component rather than two sets that drift: whichever way a report was
 * started, the second picture is added by the same three icons in the same
 * order, so nobody has to learn the widget twice.
 */

import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ScreenshotMonitorIcon from "@mui/icons-material/ScreenshotMonitor";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { CAPTURE_UI_PROPS } from "./use-support-report";

export const TILE = {
	height: 56,
	width: 84,
	borderRadius: 1,
	border: 1,
	borderColor: "divider",
} as const;

export const TILE_CENTRE = {
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
} as const;

export const TILE_BUTTON = {
	p: 0,
	bgcolor: "transparent",
	color: "text.secondary",
	cursor: "pointer",
	borderStyle: "dashed",
	"&:hover": { color: "primary.main", borderColor: "primary.main" },
} as const;

interface SupportPictureSourcesProps {
	/** A capture in flight: the tile becomes the place the picture will land. */
	isCapturing: boolean;
	/**
	 * Null where photographing the current page cannot mean anything.
	 *
	 * On /support/<id> it would only ever picture the support page — never
	 * the screen being discussed — so the tile is not offered there. Another
	 * tab or window still is, and that is the one an operator actually wants.
	 */
	onAddPicture: (() => void) | null;
	/** Null where the browser cannot offer it. */
	onAddOtherSurface: (() => void) | null;
	onUploadPicture: (file: File) => void;
	/**
	 * What the capture-this-page tile can honestly promise.
	 *
	 * The bubble floats above whatever page the reporter walks to, so it can
	 * offer the page in front of them AND the next one. The staff thread is a
	 * route: leaving it unmounts the reply being written, so there it
	 * photographs the page as it stands.
	 */
	capturePageHint?: string;
}

const SupportPictureSources: React.FC<SupportPictureSourcesProps> = ({
	isCapturing,
	onAddPicture,
	onAddOtherSurface,
	onUploadPicture,
	capturePageHint = "Picture this page — or walk to another one, this stays open, and picture that",
}) => (
	<>
		{onAddPicture === null ? null : isCapturing ? (
			// Where the picture is about to appear, so the wait has a place.
			// The panel used to be unmounted for the duration instead, which
			// read as the widget breaking.
			<Box
				sx={{ ...TILE, ...TILE_CENTRE }}
				data-component="support-capture-pending"
			>
				<CircularProgress size={18} />
			</Box>
		) : (
			<Tooltip title={capturePageHint} slotProps={{ popper: CAPTURE_UI_PROPS }}>
				<Box
					component="button"
					type="button"
					onClick={onAddPicture}
					data-component="support-report-add-picture"
					sx={{ ...TILE, ...TILE_CENTRE, ...TILE_BUTTON }}
				>
					<AddAPhotoIcon fontSize="small" />
				</Box>
			</Tooltip>
		)}

		{onAddOtherSurface && (
			<Tooltip
				title="Picture another tab, another window, or the whole screen"
				slotProps={{ popper: CAPTURE_UI_PROPS }}
			>
				<Box
					component="button"
					type="button"
					onClick={onAddOtherSurface}
					data-component="support-report-add-other-surface"
					sx={{ ...TILE, ...TILE_CENTRE, ...TILE_BUTTON }}
				>
					<ScreenshotMonitorIcon fontSize="small" />
				</Box>
			</Tooltip>
		)}

		<Tooltip
			title="Upload a picture from your computer"
			slotProps={{ popper: CAPTURE_UI_PROPS }}
		>
			<Box
				component="label"
				data-component="support-report-upload-button"
				sx={{ ...TILE, ...TILE_CENTRE, ...TILE_BUTTON }}
			>
				<FileUploadIcon fontSize="small" />
				<input
					type="file"
					accept="image/*"
					hidden
					onChange={(event) => {
						const file = event.target.files?.[0];
						event.target.value = "";
						if (file) onUploadPicture(file);
					}}
					data-component="support-upload-file-input"
				/>
			</Box>
		</Tooltip>
	</>
);

export default SupportPictureSources;
